import { cloneDeep } from 'lodash';
import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Preferences } from '../../profile/objects/preferences';
import { Column } from '../objects/column';
import { PriceCacheProvider } from '../objects/priceCacheProvider';
import { PriceCacheProviderListCriteria } from '../objects/priceCacheProviderList';
import { PriceCacheState } from '../objects/priceCacheState';
import { EMPTY_PRICE_CACHE_PROVIDER_LIST_CONFIGURATION, EMPTY_PRICE_CACHE_PROVIDER_LIST_CRITERIA } from '../constant/priceCacheConstant';
import { setPriceCacheProviderPreference } from '../utils/setPriceCacheProviderPreference';

// Action émise par le slice profile — interceptée par toutes les features via extraReducers
export const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');

const initialState: PriceCacheState = {
    price_cache_provider_list: [],
    price_cache_provider_list_loading: false,
    price_cache_provider_list_criteria: EMPTY_PRICE_CACHE_PROVIDER_LIST_CRITERIA,
    price_cache_provider_list_configuration: EMPTY_PRICE_CACHE_PROVIDER_LIST_CONFIGURATION,
    price_cache_provider: null,
    price_cache_provider_refresh: false,
    first_preference_loaded: false,
};

// ── Reducer functions ─────────────────────────────────────────────────────────

const updatePriceCacheProviderListReducer = (state: PriceCacheState, action: PayloadAction<PriceCacheProvider[]>): PriceCacheState =>
    ({ ...state, price_cache_provider_list: action.payload });

const updatePriceCacheProviderListLoadingReducer = (state: PriceCacheState, action: PayloadAction<boolean>): PriceCacheState =>
    ({ ...state, price_cache_provider_list_loading: action.payload });

const updatePriceCacheProviderListCriteriaReducer = (state: PriceCacheState, action: PayloadAction<PriceCacheProviderListCriteria>): PriceCacheState =>
    ({ ...state, price_cache_provider_list_criteria: action.payload });

const togglePriceCacheProviderRefreshReducer = (state: PriceCacheState): PriceCacheState =>
    ({ ...state, price_cache_provider_refresh: !state.price_cache_provider_refresh });

const updatePriceCacheProviderReducer = (state: PriceCacheState, action: PayloadAction<PriceCacheProvider | null>): PriceCacheState =>
    ({ ...state, price_cache_provider: action.payload });

const setPriceCacheProviderColumnsReducer = (state: PriceCacheState, action: PayloadAction<Column[]>): PriceCacheState => ({
    ...state,
    price_cache_provider_list_configuration: { ...state.price_cache_provider_list_configuration, columns: action.payload },
});

const togglePriceCacheProviderColumnReducer = (state: PriceCacheState, action: PayloadAction<number>): PriceCacheState => {
    const columns = cloneDeep([...state.price_cache_provider_list_configuration.columns]);
    columns[action.payload] = { ...columns[action.payload], checked: !columns[action.payload].checked };
    return { ...state, price_cache_provider_list_configuration: { ...state.price_cache_provider_list_configuration, columns } };
};

const setPriceCacheProviderListConfigurationPreferenceReducer = (state: PriceCacheState, action: PayloadAction<Preferences[]>): PriceCacheState => ({
    ...state,
    price_cache_provider_list_configuration: { ...state.price_cache_provider_list_configuration, preferences: action.payload },
});

const addPriceCacheProviderListConfigurationPreferenceReducer = (state: PriceCacheState, action: PayloadAction<Preferences[]>): PriceCacheState => {
    const preferences = cloneDeep([...state.price_cache_provider_list_configuration.preferences]);
    action.payload.forEach(p => preferences.push(p));
    return { ...state, price_cache_provider_list_configuration: { ...state.price_cache_provider_list_configuration, preferences } };
};

// Chargement initial des préférences depuis le store global profile
const setPreferenceReducer = (state: PriceCacheState, action: PayloadAction<Preferences[]>): PriceCacheState => {
    const pref = setPriceCacheProviderPreference(action.payload, state.price_cache_provider_list_configuration.columns);
    return {
        ...state,
        first_preference_loaded: true,
        price_cache_provider_list_criteria: { ...state.price_cache_provider_list_criteria, order_by: pref.order_by, order: pref.order },
        price_cache_provider_list_configuration: { columns: pref.columns, preferences: pref.preferences },
    };
};

// ── Slice ─────────────────────────────────────────────────────────────────────

export const priceCache = createSlice({
    name: 'price_cache',
    initialState,
    reducers: {
        updatePriceCacheProviderList: updatePriceCacheProviderListReducer,
        updatePriceCacheProviderListLoading: updatePriceCacheProviderListLoadingReducer,
        updatePriceCacheProviderListCriteria: updatePriceCacheProviderListCriteriaReducer,
        togglePriceCacheProviderRefresh: togglePriceCacheProviderRefreshReducer,
        updatePriceCacheProvider: updatePriceCacheProviderReducer,
        setPriceCacheProviderColumns: setPriceCacheProviderColumnsReducer,
        togglePriceCacheProviderColumn: togglePriceCacheProviderColumnReducer,
        setPriceCacheProviderListConfigurationPreference: setPriceCacheProviderListConfigurationPreferenceReducer,
        addPriceCacheProviderListConfigurationPreference: addPriceCacheProviderListConfigurationPreferenceReducer,
    },
    extraReducers: (builder) => {
        builder.addCase(setPreferences, setPreferenceReducer);
    },
});

export const {
    updatePriceCacheProviderList,
    updatePriceCacheProviderListLoading,
    updatePriceCacheProviderListCriteria,
    togglePriceCacheProviderRefresh,
    updatePriceCacheProvider,
    setPriceCacheProviderColumns,
    togglePriceCacheProviderColumn,
    setPriceCacheProviderListConfigurationPreference,
    addPriceCacheProviderListConfigurationPreference,
} = priceCache.actions;

export default priceCache.reducer;
